import React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useGetStats } from "../queries/statsQueries";
import { getStatsColumns, getStatsMobileColumns } from "../stats/statsTableConfig";
import { SitesResponse, SportsResponse } from "../../common/store/types";
import DataGrid from "../../common/Table/newTable";
import { GridColDef } from "@mui/x-data-grid";

interface AllProjectionsProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite?: SitesResponse;
  readonly tableHeight?: string;
  readonly backgroundColor?: string;
  readonly isUserSubscribed?: boolean;
}

const AllProjections = (props: AllProjectionsProps) => {
  const { selectedSport, selectedSite, tableHeight, backgroundColor, isUserSubscribed = true } = props;

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Extract sport and site strings from Redux props
  const sport = selectedSport?.sport || '';
  const site = selectedSite?.site || 'DK';

  // Fetch stats data with site parameter
  const {
    data: statsData,
    isLoading,
    error,
  } = useGetStats(
    sport,
    'projections',
    isUserSubscribed && (sport === 'NBA' || sport === 'MLB'),
    site
  );

  const columns: GridColDef[] = React.useMemo(
    () => isMobile
      ? getStatsMobileColumns(sport, 'projections')
      : getStatsColumns(sport, 'projections'),
    [sport, isMobile]
  );

  // Transform data to include proper id field for DataGrid
  const rows = React.useMemo(() => {
    return (statsData || []).map((row: any, index: number) => ({
      ...row,
      id: row.id || index + 1,
      nameAndId: row.name || `${index + 1}`,
    }));
  }, [statsData]);

  // Handle subscription check
  if (!isUserSubscribed) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={tableHeight || "100%"}
        sx={{ backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)" }}
      >
        Please subscribe to access projections data.
      </Box>
    );
  }

  const supportedSports = ['NBA', 'MLB'];
  if (!supportedSports.includes(sport)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={tableHeight || "100%"}
        sx={{ backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)" }}
      >
        All Projections is not available for {sport}.
      </Box>
    );
  }

  return (
    <Box
      height={tableHeight || "100%"}
      sx={{ backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)" }}
    >
      <DataGrid
        lineups={rows}
        headers={columns}
        isLoading={isLoading}
        selectedView="stats"
        simType="dataHub"
        includeStacks={false}
        setStackView={() => {}}
        tierlocked={false}
        setPlayerList={() => {}}
        setStackList={() => {}}
        pagination
        rowHeight={isMobile ? 52 : 52}
        fontSize={isMobile ? 12 : 14}
        emptyText={error ? "Error loading projections. Please try again." : `No projections available for ${sport}.`}
        backgroundColor={backgroundColor || "rgba(125, 125, 125, .25)"}
        isMobile={isMobile}
        pinnedColumnsLeft={isMobile ? ["name"] : undefined}
      />
    </Box>
  );
};

export default AllProjections;
